import React, { useState } from 'react';
import { IProviderReport } from '../models';
import { OutlinedTextInput, PrimaryButton, SizeableRoundedDialog, Text } from 'library';
import { Grid } from '@material-ui/core';
import { formatShortWeekdayMonthDay } from 'shared/Dates';
import { approveReport, getTimestampByType } from '../HourReportMethods';
import { MuiPickersUtilsProvider, TimePicker } from '@material-ui/pickers';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import DateFnsUtils from '@date-io/date-fns';

export default function ChangeModal({
    changeReport,
    setChangeReport,
    updateHoursReported,
    fullScreen,
}: {
    changeReport: IProviderReport;
    setChangeReport: (r: IProviderReport | undefined) => void;
    updateHoursReported: () => void;
    fullScreen: boolean;
}) {
    const [loading, setLoading] = useState(false);
    function changeReportTime(d: Date | MaterialUiPickersDate, field: string) {
        if (d) {
            setChangeReport({ ...changeReport, [field]: d as Date });
        }
    }

    const hasTimestamps = changeReport.provider_break_end || changeReport.provider_break_start;

    const breakText =
        changeReport.break > 0
            ? `Provider reported a ${changeReport.break} minute break, if this is incorrect use the
    selection below to update the times.`
            : 'Provider did not report a break, if they took a break use the selection below to update the times.';

    async function submitChangeRequest() {
        if (changeReport) {
            setLoading(true);
            const body = {
                business_clock_in: changeReport.business_clock_in ?? undefined,
                business_clock_out: changeReport.business_clock_out ?? undefined,
                business_break_start: changeReport.business_break_start ?? undefined,
                business_break_end: changeReport.business_break_end ?? undefined,
            };
            approveReport(changeReport.id, body)
                .then(() => {
                    setChangeReport(undefined);
                })
                .finally(updateHoursReported);
        }
    }

    const breakStart = changeReport.business_break_start ?? changeReport.provider_break_start;
    const breakEnd = changeReport.business_break_end ?? changeReport.provider_break_end;
    return (
        <SizeableRoundedDialog
            open={!!changeReport}
            onClose={() => setChangeReport(undefined)}
            fullScreen={fullScreen}
            closeButton
            maxWidth="lg"
        >
            <Grid container item direction="column" style={{ padding: 20, gap: 20 }}>
                <Text variant="h1">
                    Change {changeReport?.providerName}'s hours for{' '}
                    {formatShortWeekdayMonthDay(changeReport.start ?? new Date())}
                </Text>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                    <TimePicker
                        value={
                            changeReport.business_clock_in
                                ? new Date(changeReport.business_clock_in)
                                : changeReport.start
                        }
                        style={{ maxWidth: 200 }}
                        onChange={(d: MaterialUiPickersDate) => changeReportTime(d, 'business_clock_in')}
                        helperText="Start Time"
                    />
                    <TimePicker
                        value={
                            changeReport.business_clock_out
                                ? new Date(changeReport.business_clock_out)
                                : changeReport.end
                        }
                        style={{ maxWidth: 200 }}
                        onChange={(d: MaterialUiPickersDate) => changeReportTime(d, 'business_clock_out')}
                        helperText="End Time"
                    />
                    <Text>{breakText}</Text>
                    {!hasTimestamps && changeReport.break > 0 ? (
                        <Text variant="caption">
                            Provider reported this break as we transitioned to the new process for clocking in and out
                            for breaks so please excuse if the timestamps are not reflective of the exact times the
                            provider may have taken a break.
                        </Text>
                    ) : null}
                    <TimePicker
                        value={breakStart ? new Date(breakStart) : new Date()}
                        style={{ maxWidth: 200 }}
                        onChange={(d: MaterialUiPickersDate) => changeReportTime(d, 'business_break_start')}
                        helperText="Break Start"
                    />
                    <TimePicker
                        value={breakEnd ? new Date(breakEnd) : new Date()}
                        style={{ maxWidth: 200 }}
                        onChange={(d: MaterialUiPickersDate) => changeReportTime(d, 'business_break_end')}
                        helperText="Break End"
                    />
                </MuiPickersUtilsProvider>
                <PrimaryButton disabled={loading} onClick={submitChangeRequest}>
                    Confirm
                </PrimaryButton>
            </Grid>
        </SizeableRoundedDialog>
    );
}
